import {
  MagnifyingGlass,
  FunnelSimple,
  ArrowsDownUp,
  X,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const Pill = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`h-8 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
      active
        ? "border-indigo-500 bg-indigo-500/20 text-indigo-200"
        : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"
    }`}
  >
    {children}
  </button>
);

const Chip = ({ label, onClear, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClear}
    className="flex h-8 items-center gap-1.5 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
  >
    {label}
    <X size={11} weight="bold" />
  </button>
);

const labelFor = (section, value) => {
  const hit = (section.options || []).find(([v]) => v === value);
  return hit ? hit[1] : String(value);
};

const slug = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

// Selected values for a section, normalised to an array (empty array = "all").
const selectedValues = (s) =>
  s.multi ? s.values || [] : s.value === s.allValue ? [] : [s.value];

const isPillActive = (s, value) => {
  const picked = selectedValues(s);
  if (value === s.allValue) return picked.length === 0;
  return picked.includes(value);
};

// Clicking a pill: single-select swaps the value; multi-select toggles it, and
// choosing the "all" pill clears the whole section back to no filter.
const pickValue = (s, value) => {
  if (!s.multi) {
    s.onChange(value);
    return;
  }
  if (value === s.allValue) {
    s.onChange([]);
    return;
  }
  const cur = s.values || [];
  s.onChange(
    cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
  );
};

const clearValue = (s, value) => {
  if (!s.multi) {
    s.onChange(s.allValue);
    return;
  }
  s.onChange((s.values || []).filter((v) => v !== value));
};

const clearSection = (s) => s.onChange(s.multi ? [] : s.allValue);

/**
 * Search box plus a single Filters button holding every section, with the
 * chosen filters shown as removable chips underneath.
 *
 * sections: [{ key, label, allValue, sort?, chipPrefix?, testid?,
 *              options: [[value, label, testid]],
 *              // single-select:
 *              value, onChange(value)
 *              // OR multi-select:
 *              multi: true, values: [], onChange(nextValues) }]
 */
export const FilterBar = ({
  search,
  sections = [],
  testidPrefix,
  right = null,
  className = "",
}) => {
  const chips = sections.flatMap((s) =>
    selectedValues(s).map((v) => ({ s, v })),
  );

  const clearAll = () => sections.forEach(clearSection);

  return (
    <div className={className}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {search && (
          <div className="relative flex-1">
            <MagnifyingGlass
              size={17}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid={search.testid}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder}
              className="h-12 border-zinc-800 bg-zinc-900 pl-9"
            />
          </div>
        )}

        {sections.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`${testidPrefix}-filter-button`}
                className="flex h-12 shrink-0 items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-zinc-700"
              >
                <FunnelSimple size={15} weight="bold" />
                Filters
                {chips.length > 0 && (
                  <span
                    data-testid={`${testidPrefix}-filter-count`}
                    className="flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1.5 text-[10px] font-bold text-white"
                  >
                    {chips.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              data-testid={`${testidPrefix}-filter-panel`}
              className="max-h-[70vh] w-[19rem] overflow-y-auto border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="space-y-4">
                {sections.map((s) => (
                  <div key={s.key} data-testid={s.testid}>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {s.sort && <ArrowsDownUp size={12} weight="bold" />}
                      {s.label}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.options.map(([value, label, testid]) => (
                        <Pill
                          key={String(value)}
                          testid={testid}
                          active={isPillActive(s, value)}
                          onClick={() => pickValue(s, value)}
                        >
                          {label}
                        </Pill>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  data-testid={`${testidPrefix}-filter-clear`}
                  onClick={clearAll}
                  disabled={chips.length === 0}
                  className="w-full border-t border-zinc-800 pt-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:text-red-300 disabled:opacity-40"
                >
                  Clear all filters
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {right}
      </div>

      {chips.length > 0 && (
        <div
          className="mt-3 flex flex-wrap items-center gap-2"
          data-testid={`${testidPrefix}-active-filters`}
        >
          {chips.map(({ s, v }) => (
            <Chip
              key={`${s.key}-${v}`}
              testid={
                s.multi
                  ? `${testidPrefix}-chip-${s.key}-${slug(v)}`
                  : `${testidPrefix}-chip-${s.key}`
              }
              label={`${s.chipPrefix || ""}${labelFor(s, v)}`}
              onClear={() => clearValue(s, v)}
            />
          ))}
          <button
            type="button"
            data-testid={`${testidPrefix}-clear-chips`}
            onClick={clearAll}
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
